import React from "react";

import { API_ENDPOINT } from "../../../constants/appConstant";
import { SalaryModeOptions } from "../../../constants/employment";
import { getMonthNumber } from "../../../helpers/getMonthNumber";
import { getNoticePeriodToDays } from "../../../helpers/getNoticePeriod";
import { extractValues } from "../../../helpers/helper";
import { shouldShowSalaryNudge } from "../../../helpers/shouldShowSalaryNudge";
import { useNetwork } from "../../../hooks/useNetwork";
import { ApopProps } from "../../../types";
import { getSiteProps } from "../../../utils/siteProps";
import { Events } from "../common/json/clevertapEvents";
import { EmploymentApopUI } from "./EmploymentUI";

interface UpdatedData {
  [key: string]: any;
}

export const EmploymentApop = ({
  nextApopType,
  onCTA,
  toast,
  prefilledData,
  closeApop,
  flowType,
  backBtnClickHandler,
  handleEventTracking,
  eventName,
}: ApopProps) => {
  const { response: apiResponse, fetchData: submitFetchApi } = useNetwork();
  const isShowHideSalaryToggle = false;

  const onSubmit = async (
    data: any,
    salaryMode: (typeof SalaryModeOptions)[0],
    isCurrentlyWorking: boolean,
    isAutoUpdateChecked: boolean,
    isRexMonster: boolean,
  ) => {
    const siteConfig = getSiteProps(window.location.hostname);
    const updatedData: UpdatedData = {
      employments: [
        {
          startDate: `${data.startYear.text}-${getMonthNumber(data.startMonth.text)}-01`,
          salary: {
            currency: data.currency.text,
            absoluteValue: data.salary,
            ...(isShowHideSalaryToggle
              ? { confidential: data.hideSalary }
              : {}),
            salaryMode: {
              id: salaryMode.uuid,
            },
          },
          ...(data.industries
            ? {
                industries: extractValues(data.industries, ["id", "text"]),
              }
            : {}),
        },
      ],
    };

    if (isCurrentlyWorking && data.notice) {
      if (data?.notice?.text === "Serving Notice Period") {
        updatedData["employments"][0]["noticePeriod"] = {
          days: getNoticePeriodToDays(data.notice.text),
          serving: true,
          lastWorkingDay: data.date,
        };
      } else {
        updatedData["employments"][0]["noticePeriod"] = {
          days: getNoticePeriodToDays(data?.notice?.text),
          serving: false,
          lastWorkingDay: null,
        };
      }
    }

    if (!isCurrentlyWorking) {
      updatedData["employments"][0]["endDate"] =
        `${data.endYear.text}-${getMonthNumber(data.endMonth.text)}-01`;
    }

    if (data?.jobTitle?.text === "Others") {
      updatedData["employments"][0]["designation"] = {
        text: data.manualCurrentJobTitle,
      };
    } else {
      updatedData["employments"][0]["designation"] = {
        id: data.jobTitle.id,
      };
    }

    if (data?.company?.text === "Other") {
      updatedData["employments"][0]["company"] = {
        text: data.manualCompany,
      };
    } else {
      updatedData["employments"][0]["company"] = {
        id: data.company.id,
      };
    }

    if (
      data?.notice?.text === "Serving Notice Period" &&
      data.offeredJobTitle
    ) {
      if (data?.offeredJobTitle?.text === "Others") {
        updatedData["employments"][0]["offeredDesignation"] = {
          text: data.manualOfferedJobTitle,
        };
      } else {
        updatedData["employments"][0]["offeredDesignation"] = {
          id: data.offeredJobTitle.id,
        };
      }

      if (data?.newCompany?.text) {
        if (data.newCompany.text === "Other") {
          updatedData["employments"][0]["newCompany"] = {
            text: data.manualNewCompany,
          };
        } else {
          updatedData["employments"][0]["newCompany"] = {
            id: data.newCompany.id,
          };
        }
      }

      if (data?.newOfferedCurrency) {
        updatedData["employments"][0]["offeredSalary"] = {
          currency: data.newOfferedCurrency.text,
          absoluteValue: data.newSalary,
          ...(isShowHideSalaryToggle
            ? { confidential: data.hidePotentialSalary }
            : {}),
          salaryMode: {
            id: salaryMode.uuid,
          },
        };
      }
    }

    if (data.linkedin) {
      updatedData["socialPresence"] = {
        url: data.linkedin,
        name: "LinkedIn",
      };
      handleEventTracking({
        eventType: "eventPush",
        eventName: Events["LINKED_IN"].linkedInClick["EVENTNAME"],
        eventProps: Events["LINKED_IN"].linkedInClick["EVENTOBJ"],
      });
    }

    if (data.isToggled) {
      updatedData["profileAutoUpdate"] = {
        linkedInConsent: true,
      };
    } else {
      updatedData["profileAutoUpdate"] = {
        linkedInConsent: false,
        reason: "OTHER_REASONS",
      };
    }

    eventName && (updatedData["eventName"] = eventName);

    handleEventTracking({
      eventType: "eventPush",
      eventName: Events["EMPLOYMENT_DETAILS"].submitEvent["EVENTNAME"],
      eventProps: Events["EMPLOYMENT_DETAILS"].submitEvent["EVENTOBJ"],
    });

    let employmentSubmitAPIURL =
      API_ENDPOINT().seekerProfile.employmentDetailsMiddleware;
    let employmentSubmitAPIMethod: "POST" | "PUT" = "POST";

    // This logic is moved out of the if condition of PROFILE_NUDGE
    // because there is a possibility that user's data might be coming from edge
    // and it may contain an id which is present in the prefilledData
    if (
      prefilledData &&
      prefilledData?.employmentPrefill &&
      (prefilledData?.employmentPrefill as any)?.id
    ) {
      updatedData.employments[0].id = (
        prefilledData?.employmentPrefill as any
      ).id;

      employmentSubmitAPIURL =
        API_ENDPOINT().seekerProfile.updateEmploymentDetailsMiddleware;
      employmentSubmitAPIMethod = "PUT";
    } else {
      employmentSubmitAPIURL =
        API_ENDPOINT().seekerProfile.employmentDetailsProfileNudge;
      employmentSubmitAPIMethod = "POST";
    }

    if (flowType === "PROFILE_NUDGE") {
      const eventTracking = (isExperienced: boolean) => {
        let shortHand = Events["RESUMEANDEMPLOYMENT"].editInfo["EVENTOBJ"];
        shortHand.uploadResume =
          prefilledData && prefilledData?.resumePrefill ? "Yes" : "No";
        shortHand.typeOfUpdate = isExperienced ? "Edit & Update" : "Add";
        shortHand.autoUpdate = isAutoUpdateChecked ? "Yes" : "No";
        shortHand.fresher = isExperienced ? "No" : "Yes";

        handleEventTracking({
          eventType: "eventPush",
          eventName: Events["RESUMEANDEMPLOYMENT"]?.editInfo?.["EVENTNAME"],
          eventProps: Events["RESUMEANDEMPLOYMENT"]?.editInfo?.["EVENTOBJ"],
        });
      };

      eventTracking(
        prefilledData &&
          prefilledData?.employmentPrefill &&
          (prefilledData?.employmentPrefill as any)?.id,
      );
    }

    const res = await submitFetchApi(
      employmentSubmitAPIURL,
      employmentSubmitAPIMethod,
      {},
      updatedData,
    );

    // handle nextAction coming from API
    if (res.data) {
      if (flowType === "PROFILE_NUDGE") {
        if (data.jobTitle.text !== "Others") {
          updatedData["employments"][0]["designation"] = {
            ...updatedData["employments"][0]["designation"],
            text: data.jobTitle.text,
          };
        }

        if (data.company.text !== "Other") {
          updatedData["employments"][0]["company"] = {
            ...updatedData["employments"][0]["company"],
            text: data.company.text,
          };
        }

        const newEmploymentId =
          (res.data as any).employmentIds &&
          (res.data as any).employmentIds.length > 0
            ? (res.data as any).employmentIds[0]
            : prefilledData?.employmentPrefill &&
                (prefilledData?.employmentPrefill as any)?.id
              ? (prefilledData?.employmentPrefill as any)?.id
              : null;

        interface PrefillData {
          employmentPrefill: any;
          linkedInResponse: {
            url: any;
            linkedInConsent: any;
          };
          resumePrefill?: any;
        }

        let newPrefillData: PrefillData = {
          employmentPrefill: {
            ...updatedData.employments[0],
            id: newEmploymentId,
          },
          linkedInResponse: {
            url: data.linkedin ?? "",
            linkedInConsent:
              updatedData?.profileAutoUpdate?.linkedInConsent ?? null,
          },
        };

        if (prefilledData && prefilledData?.resumePrefill) {
          newPrefillData = {
            ...newPrefillData,
            resumePrefill: {
              ...(typeof prefilledData?.resumePrefill === "object"
                ? prefilledData.resumePrefill
                : {}),
            },
          };
        }

        const salaryData = updatedData.employments[0].salary;
        if (shouldShowSalaryNudge(salaryData.absoluteValue, isRexMonster)) {
          nextApopType("SALARY", {
            actionType: "SALARY",
            nextActionData: (res.data as any)?.nextActionRequired ?? null,
            salaryPrefill: {
              employmentId: newEmploymentId,
              salary: {
                absoluteValue: salaryData.absoluteValue,
                currency: salaryData.currency,
                confidential: salaryData.confidential ?? false,
                salaryMode: {
                  uuid: salaryMode.uuid,
                  text: salaryMode.name,
                },
              },
            },
          });
        } else {
          onCTA && onCTA("EMPLOYMENT");
          nextApopType("UPDATE_RESUME_AND_EMPLOYMENT", newPrefillData);
        }

        backBtnClickHandler && backBtnClickHandler();
        toast &&
          toast({
            showToast: true,
            text: (res.data as any).successMessage ?? "",
            type: "success",
          });
        // data is inside rest.data for profile nudge flow
      } else {
        const salaryData = updatedData.employments[0].salary;

        if (shouldShowSalaryNudge(salaryData.absoluteValue, isRexMonster)) {
          nextApopType("SALARY", {
            actionType: "SALARY",
            nextActionData: (res.data as any)?.nextActionRequired ?? null,
            salaryPrefill: {
              salary: {
                absoluteValue: salaryData.absoluteValue,
                currency: salaryData.currency,
                confidential: salaryData.confidential ?? false,
                salaryMode: {
                  uuid: salaryMode.uuid,
                  text: salaryMode.name,
                },
              },
            },
          });
        } else {
          onCTA && onCTA("EMPLOYMENT");
          nextApopType(
            (res.data as any)?.nextActionRequired?.actionType ?? "",
            (res.data as any)?.nextActionRequired ?? null,
          );
        }

        toast &&
          toast({
            showToast: true,
            text:
              (res.data as any)?.successMessage ??
              "Section updated successfully",
            type: "success",
          });
      }

      if (data.linkedin) {
        handleEventTracking({
          eventType: "eventPush",
          eventName: Events["LINKED_IN"].linkedInAdded["EVENTNAME"],
          eventProps: Events["LINKED_IN"].linkedInAdded["EVENTOBJ"],
        });
      }
    } else {
      onCTA && onCTA("EMPLOYMENT");
      toast &&
        toast({
          showToast: true,
          text:
            (res.error as any)?.error?.message?.errorMessage ??
            "Something went wrong",
          type: "error",
        });
    }
  };

  return (
    <EmploymentApopUI
      prefilledData={prefilledData}
      closeApop={closeApop}
      flowType={flowType}
      backBtnClickHandler={backBtnClickHandler}
      onSubmit={onSubmit}
      isSubmitAPILoading={apiResponse.isLoading}
    />
  );
};
